import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export const useGibRating = (userId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: gibRating, isLoading } = useQuery({
    queryKey: ['gib-rating', userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .from('user_gib_ratings')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      return data;
    },
    enabled: !!userId,
  });

  const calculateGibRating = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { data, error } = await supabase.functions.invoke('calculate-gib-rating', {
        body: { userId: targetUserId }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gib-rating'] });
      toast({
        title: "GibRating Updated",
        description: "Your GibRating has been recalculated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to calculate GibRating",
        variant: "destructive",
      });
    },
  });

  return {
    gibRating,
    isLoading,
    calculateGibRating: calculateGibRating.mutate,
    isCalculating: calculateGibRating.isPending,
  };
};
